import type { Family, FamilyGroup, OwnerParticipant } from "@teamna/shared";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import {
  Button,
  ChatIcon,
  EmptyState,
  ErrorState,
  InlineError,
  LockIcon,
  Notice,
  PageHeader,
  SkeletonList,
  Tabs,
  panelId,
  tabId,
} from "../../components/index.ts";
import { useOpenMemberThread } from "@teamna/web-client/hooks/use-messaging.ts";
import { useActiveBusiness, useParticipantProfile } from "@teamna/web-client/hooks/use-owner.ts";
import { useFamilies } from "@teamna/web-client/hooks/use-families.ts";
import { useParticipants } from "@teamna/web-client/hooks/use-setup.ts";
import { formatMoney } from "@teamna/web-client/lib/format.ts";
import { useFamilyActions, useFamilyWords } from "../families/actions.ts";
import { InviteBadge } from "../families/bits.tsx";
import { capital, familyName, phoneText, type Words } from "../families/model.ts";
import { ChildCard, ChildSessions } from "./children.tsx";
import { ClientCard } from "./client.tsx";
import { EndManyPausesPanel, FreezeManyPanel } from "./freeze.tsx";
import { FamilyMoney, payableItems, type FamilyMoneyFacts, type FamilyPerson } from "./money.tsx";
import { PayLinkPanel, type PayLinkItem } from "./panels.tsx";
import { FamilySettings } from "./settings.tsx";

/**
 * One parent's page (owner menu revamp, 2026-10-09): everything about one family in one place.
 * It replaces the member profile and the family editor of the Families page.
 *
 * The header is the person who books and pays: name, numbers, the invite, Message, and "Send a
 * payment link" for what the family owes. Under it, one line of numbers (owed, and each child's
 * sessions left), then four tabs:
 *
 * - Overview: one card per child (group, balance, package, the next bookings) and what is to pay.
 * - Sessions: each child's bookings, with the business cancel, and the history of their balance.
 * - Payments: what is to pay, with the payment link; record a payment, ask for an amount; the
 *   requests; and what the family paid.
 * - Settings: the adults, each child (name, age, group, note for the coaches, photo), the access,
 *   the hold, and remove the family.
 *
 * Addressed by the family account (`Family.id`, the same id the member profile used), so a family
 * that has not joined has a page too. They cannot read messages until they join, so Message is off.
 */
export type ParentTab = "overview" | "sessions" | "payments" | "settings";

export function OwnerParentScreen() {
  const { id } = useParams({ from: "/owner/parents/$id" });
  const { business, isLoading, error } = useActiveBusiness();
  const words = useFamilyWords();
  const back = { to: "/owner/parents", label: `Back to ${words.members}` };

  if (business) {
    return (
      <div>
        <ParentBody
          businessId={business.businessId}
          familyId={id}
          timezone={business.timezone}
          businessName={business.name}
          cardPayments={business.cardPayments !== false}
          cardPaymentsComingSoon={business.cardPaymentsComingSoon === true}
          ownerOnly={business.staffRole === "owner"}
          words={words}
          back={back}
        />
      </div>
    );
  }
  return (
    <div>
      <PageHeader back={back} title={capital(words.member)} />
      {isLoading ? (
        <SkeletonList />
      ) : error ? (
        <ErrorState error={error} />
      ) : (
        <EmptyState icon={<LockIcon size={30} />} title="No business to manage" message="This account does not run a business yet." />
      )}
    </div>
  );
}

function ParentBody({
  businessId,
  familyId,
  timezone,
  businessName,
  cardPayments,
  cardPaymentsComingSoon = false,
  ownerOnly,
  words,
  back,
}: {
  businessId: string;
  familyId: string;
  timezone: string;
  businessName: string;
  cardPayments: boolean;
  /** Teamna has card payments off for all businesses: the card parts say "Coming soon". */
  cardPaymentsComingSoon?: boolean;
  /** Money is the owner's: the API refuses the payments register to an admin. */
  ownerOnly: boolean;
  words: Words;
  back: { to: string; label: string };
}) {
  const families = useFamilies(businessId);
  const participants = useParticipants(businessId);
  // The family account, or the second adult of a family (a link from a group's list can name either).
  const family =
    families.data?.families.find((candidate) => candidate.id === familyId) ??
    families.data?.families.find((candidate) => candidate.adults.some((adult) => adult.userId === familyId)) ??
    null;

  if (families.isLoading && !family) {
    return (
      <>
        <PageHeader back={back} title={capital(words.member)} />
        <SkeletonList />
      </>
    );
  }
  // The children's balances, packages and money come from the participants read: until it answers,
  // the page cannot tell "no swimmers" from "not read yet", so it waits or says the read failed.
  if (family && participants.isLoading && !participants.data) {
    return (
      <>
        <PageHeader back={back} title={familyName(family)} />
        <SkeletonList />
      </>
    );
  }
  if (family && participants.error && !participants.data) {
    return (
      <>
        <PageHeader back={back} title={familyName(family)} />
        <ErrorState error={participants.error} onRetry={() => void participants.refetch()} />
      </>
    );
  }
  if (!family || !families.data) {
    return (
      <>
        <PageHeader back={back} title={capital(words.member)} />
        {families.error ? (
          <ErrorState error={families.error} onRetry={() => void families.refetch()} />
        ) : (
          <EmptyState
            title={`No such ${words.member}`}
            message={`This ${words.member} is not at this business. Go back to the list and choose again.`}
          />
        )}
      </>
    );
  }

  const byId = new Map((participants.data ?? []).map((participant) => [participant.id, participant]));
  const children = family.children
    // Where clients book for themselves the page shows the client's own record only, never an older
    // child row from before a change of who books (C1 review R2.2).
    .filter((child) => words.forChildren || child.kind !== "child")
    .map((child) => byId.get(child.participantId))
    .filter((participant): participant is OwnerParticipant => participant !== undefined);

  return (
    <FamilyPage
      businessId={businessId}
      family={family}
      groups={families.data.groups}
      participants={children}
      onReloadParticipants={() => void participants.refetch()}
      timezone={timezone}
      businessName={businessName}
      cardPayments={cardPayments}
      cardPaymentsComingSoon={cardPaymentsComingSoon}
      ownerOnly={ownerOnly}
      words={words}
      back={back}
    />
  );
}

function FamilyPage({
  businessId,
  family,
  groups,
  participants: children,
  onReloadParticipants,
  timezone,
  businessName,
  cardPayments,
  cardPaymentsComingSoon = false,
  ownerOnly,
  words,
  back,
}: {
  businessId: string;
  family: Family;
  groups: FamilyGroup[];
  /** The participant record of each child the participants read lists. */
  participants: OwnerParticipant[];
  onReloadParticipants: () => void;
  timezone: string;
  businessName: string;
  cardPayments: boolean;
  cardPaymentsComingSoon?: boolean;
  ownerOnly: boolean;
  words: Words;
  back: { to: string; label: string };
}) {
  const navigate = useNavigate();
  // The front desk (T1) has no Settings tab: every change there is the owner's.
  const { may } = useActiveBusiness();
  const actions = useFamilyActions(businessId);
  const openThread = useOpenMemberThread(businessId);
  const [tab, setTab] = useState<ParentTab>("overview");
  const [links, setLinks] = useState<PayLinkItem[] | null>(null);
  // Freeze all on a family (D17): every child and every pack. Each child card has its own Freeze.
  const [freezeAll, setFreezeAll] = useState(false);
  const [endAll, setEndAll] = useState(false);
  // Owed packages and payment requests belong to the family, so any child's profile carries them.
  // The family's own list names the child, so a child the participants read does not list yet still counts.
  const profile = useParticipantProfile(businessId, family.children[0]?.participantId ?? children[0]?.id ?? null);
  const owed = profile.data?.owed ?? [];
  const requests = profile.data?.requests ?? [];
  const payable = payableItems(owed, requests);
  const owedFils = payable.reduce((total, item) => total + item.amountFils, 0);
  // The packs belong to the family account, so any child's profile lists all of them.
  const familyPacks = profile.data?.packs ?? [];
  const freezable = familyPacks.filter((pack) => pack.canFreeze && !pack.pause);
  const paused = familyPacks.filter((pack) => pack.pause);
  const anyChildId = family.children[0]?.participantId ?? children[0]?.id ?? null;

  const name = familyName(family);
  const memberName = family.adults[0]?.name || name;
  // The family's own list is the truth for who the children are. A child the participants read does
  // not list yet (a stale read) is still a child: the money actions take it by its id, and its card
  // says its details are not ready, with a way to read them again.
  const byId = new Map(children.map((child) => [child.id, child]));
  // The same rule as the cards: where clients book for themselves, an older child row is not offered
  // (for example as "A new package for ..." in Record a payment).
  const listed = family.children.filter((child) => words.forChildren || child.kind !== "child");
  const people: FamilyPerson[] = listed.map((child) => ({
    id: child.participantId,
    displayName: byId.get(child.participantId)?.displayName ?? child.name,
    groupId: byId.get(child.participantId)?.groupId ?? null,
  }));
  const notReady = listed.filter((child) => !byId.has(child.participantId));
  const joined = family.invite === "joined";
  const numbers = family.adults
    .filter((adult) => adult.phoneE164)
    .map((adult) => phoneText(adult.phoneE164))
    .join(", ");

  const money: FamilyMoneyFacts = {
    owed,
    requests,
    loading: profile.isLoading,
    error: profile.error,
    payable,
    cardPayments,
    cardPaymentsComingSoon,
    accountParticipantId: anyChildId,
    refresh: () => profile.refetch(),
    // The membership is the profile's own participant's: the first child, or the client.
    renewal:
      profile.data?.membership?.renewal && anyChildId
        ? { participantId: anyChildId, name: people.find((person) => person.id === anyChildId)?.displayName ?? memberName, membership: profile.data.membership }
        : null,
  };

  function message() {
    openThread.mutate(family.id, {
      onSuccess: (thread) => void navigate({ to: "/owner/inbox/$conversationId", params: { conversationId: thread.id } }),
    });
  }

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "sessions", label: "Sessions" },
    { id: "payments", label: "Payments", ...(payable.length ? { count: payable.length } : {}) },
    ...(may("editFamily") ? [{ id: "settings", label: "Settings" }] : []),
  ];

  return (
    <>
      <PageHeader
        back={back}
        eyebrow={capital(words.member)}
        title={name}
        description={`${numbers || "No number"}. ${people.length} ${people.length === 1 ? words.participant : words.participants}.`}
        actions={
          <>
            <InviteBadge state={family.invite} />
            <Button
              variant="secondary"
              disabled={!joined}
              loading={openThread.isPending}
              onClick={message}
              title={joined ? undefined : "They have not joined yet, so they cannot read messages."}
            >
              <ChatIcon size={17} />
              Message
            </Button>
            {cardPayments && payable.length > 0 ? <Button onClick={() => setLinks(payable)}>Send a payment link</Button> : null}
          </>
        }
      />
      {!joined ? (
        <div className="mb-4">
          <Notice tone="info" title="Not joined yet">
            {family.invite === "invited"
              ? `They have the invite and did not join yet. They see these ${words.participants} and balances when they join.`
              : `They are not invited yet. Send the invite from the ${capital(words.members)} page when their ${words.participants} have a ${words.group}.`}{" "}
            Until then you cannot message them here.
          </Notice>
        </div>
      ) : null}
      {family.holdNote ? (
        <div className="mb-4">
          <Notice tone="warning" title="On hold">
            <span dir="auto">{family.holdNote}</span>
          </Notice>
        </div>
      ) : null}
      {openThread.error ? <InlineError error={openThread.error} /> : null}

      <dl className="mb-6 grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
        <div className="rounded-card border border-line bg-paper px-4 py-3">
          <dt className="eyebrow text-ink-2">To pay</dt>
          <dd className={`text-[20px] font-semibold tabular-nums ${owedFils > 0 ? "text-late" : "text-ink"}`}>
            {profile.isLoading ? "..." : owedFils > 0 ? formatMoney(owedFils) : "Nothing"}
          </dd>
        </div>
        {children.map((child) => (
          <div key={child.id} className="rounded-card border border-line bg-paper px-4 py-3">
            <dt className="eyebrow truncate text-ink-2" dir="auto">
              {child.displayName}
            </dt>
            <dd className="text-[20px] font-semibold tabular-nums text-ink">
              {child.packageName === null ? "No package" : child.unlimited ? "No limit" : `${child.sessionsLeft} left`}
            </dd>
            {words.forChildren ? <dd className="truncate text-[13px] text-ink-2">{child.groupName ?? `No ${words.group} yet`}</dd> : null}
          </div>
        ))}
        {notReady.map((child) => (
          <div key={child.participantId} className="rounded-card border border-line bg-paper px-4 py-3">
            <dt className="eyebrow truncate text-ink-2" dir="auto">
              {child.name}
            </dt>
            <dd className="text-[20px] font-semibold text-ink-3">Not ready</dd>
          </div>
        ))}
      </dl>
      {notReady.length > 0 ? (
        <div className="mb-6">
          <Notice
            tone="info"
            title={`The details of ${notReady.map((child) => child.name).join(" and ")} are not ready yet`}
            action={
              <div>
                <Button variant="secondary" onClick={onReloadParticipants}>
                  Read them again
                </Button>
              </div>
            }
          >
            The balance, the package and the sessions show when they are ready. You can still record a payment and ask for an
            amount.
          </Notice>
        </div>
      ) : null}

      <Tabs tabs={tabs} selectedId={tab} onSelect={(id) => setTab(id as ParentTab)} label={`${capital(words.member)} views`} />
      <div role="tabpanel" id={panelId(tab)} aria-labelledby={tabId(tab)} className="pt-5">
        {tab === "overview" ? (
          people.length === 0 ? (
            <EmptyState
              title={`No ${words.participants} yet`}
              message={
                may("editFamily")
                  ? `Add a ${words.participant} in Settings, or the ${words.member} adds one in the app.`
                  : `The ${words.member} adds one in the app, or the owner adds one.`
              }
              {...(may("editFamily")
                ? {
                    action: (
                      <Button variant="secondary" onClick={() => setTab("settings")}>
                        Add a {words.participant}
                      </Button>
                    ),
                  }
                : {})}
            />
          ) : (
            <div className="flex flex-col gap-6">
              {words.forChildren && anyChildId && ((freezable.length > 0 && may("freezeMany")) || (paused.length > 1 && may("endManyPauses"))) ? (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-[14px] text-ink-2">
                    Freeze all pauses every package of every {words.participant}. To pause one {words.participant}, use Freeze on their card.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {paused.length > 1 && may("endManyPauses") ? (
                      <Button variant="secondary" onClick={() => setEndAll(true)}>
                        End all pauses
                      </Button>
                    ) : null}
                    {freezable.length > 0 && may("freezeMany") ? (
                      <Button variant="freeze" onClick={() => setFreezeAll(true)}>
                        Freeze all
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : null}
              {children.map((child) =>
                // Where clients book for themselves the page is about the client: each pack, the visits (C1).
                words.forChildren ? (
                  <ChildCard
                    key={child.id}
                    businessId={businessId}
                    participant={child}
                    memberName={memberName}
                    timezone={timezone}
                    words={words}
                  />
                ) : (
                  <ClientCard key={child.id} businessId={businessId} participant={child} memberName={memberName} timezone={timezone} words={words} />
                ),
              )}
              {owed.length > 0 || requests.some((row) => row.status === "pending" || row.status === "overdue") ? (
                <FamilyMoney
                  businessId={businessId}
                  people={people}
                  memberName={memberName}
                  businessName={businessName}
                  timezone={timezone}
                  money={money}
                  words={words}
                  ownerOnly={ownerOnly}
                  adultIds={family.adults.map((adult) => adult.userId)}
                  summary
                  onSendLink={setLinks}
                />
              ) : null}
            </div>
          )
        ) : tab === "sessions" ? (
          children.length === 0 && people.length > 0 ? (
            <p className="text-[14px] text-ink-2">The sessions show when the details of the {words.participants} are ready.</p>
          ) : (
            <ChildSessions businessId={businessId} participants={children} memberName={memberName} timezone={timezone} words={words} />
          )
        ) : tab === "payments" ? (
          <FamilyMoney
            businessId={businessId}
            people={people}
            memberName={memberName}
            businessName={businessName}
            timezone={timezone}
            money={money}
            words={words}
            ownerOnly={ownerOnly}
            adultIds={family.adults.map((adult) => adult.userId)}
            onSendLink={setLinks}
          />
        ) : may("editFamily") ? (
          <FamilySettings
            businessId={businessId}
            family={family}
            groups={groups}
            participants={children}
            words={words}
            actions={actions}
            onRemoved={() => void navigate({ to: "/owner/parents" })}
          />
        ) : null}
      </div>

      {freezeAll && anyChildId ? (
        <FreezeManyPanel
          businessId={businessId}
          participantId={anyChildId}
          scope="all"
          title={`Freeze all packages of ${name}`}
          name={name}
          packs={freezable}
          timezone={timezone}
          onClose={() => setFreezeAll(false)}
        />
      ) : null}
      {endAll && anyChildId ? (
        <EndManyPausesPanel
          businessId={businessId}
          participantId={anyChildId}
          scope="all"
          title={`End all pauses of ${name}?`}
          onClose={() => setEndAll(false)}
        />
      ) : null}
      {links ? <PayLinkPanel items={links} memberName={memberName} businessName={businessName} onClose={() => setLinks(null)} /> : null}
    </>
  );
}
